import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, RefreshCw, Printer, Clock, Users, Wrench, Coffee, LogIn, LogOut, FileText, Sparkles, MessageSquare, AlertTriangle, CheckCircle2, Pause, ClipboardList, Truck, Car } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { API } from '@/utils/api';
import { toast } from 'sonner';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import PreparationChecklist from '@/components/PreparationChecklist';
import { useTranslation } from 'react-i18next';

const STATUS_CONFIG = {
  present: { label: 'Present', color: 'bg-emerald-500', textColor: 'text-emerald-700', bgLight: 'bg-emerald-50' },
  pause: { label: 'Pause', color: 'bg-amber-500', textColor: 'text-amber-700', bgLight: 'bg-amber-50' },
  termine: { label: 'Termine', color: 'bg-slate-400', textColor: 'text-slate-600', bgLight: 'bg-slate-50' },
  absent: { label: 'Absent', color: 'bg-red-400', textColor: 'text-red-500', bgLight: 'bg-red-50/50' },
};

const ROLE_COLORS = {
  blue: 'bg-blue-100 text-blue-700 border-blue-200',
  emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  violet: 'bg-violet-100 text-violet-700 border-violet-200',
  indigo: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  slate: 'bg-slate-100 text-slate-600 border-slate-200',
};

const formatTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' });
};

const WorkshopPlanningPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [editingNote, setEditingNote] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [activePreps, setActivePreps] = useState([]);
  const [prepDialog, setPrepDialog] = useState({ open: false, vehicleId: null, vehicleName: '', type: 'parc' });

  const loadData = useCallback(async () => {
    try {
      const [res, prepsRes] = await Promise.all([
        axios.get(`${API}/workshop/planning`, { params: { date } }),
        axios.get(`${API}/used-vehicles/preparations/active`),
      ]);
      setData(res.data);
      setActivePreps(prepsRes.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const changeDate = (days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    setDate(d.toISOString().split('T')[0]);
  };

  const saveAdminNote = async (employeeId) => {
    try {
      await axios.post(`${API}/workshop/daily-note`, { employee_id: employeeId, date, admin_note: noteText });
      toast.success('Note enregistree');
      setEditingNote(null);
      loadData();
    } catch { toast.error('Erreur'); }
  };

  const isToday = date === new Date().toISOString().split('T')[0];

  if (loading || !data) return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-6 text-center text-slate-400">{t('common.loading')}</div>
    </AdminLayout>
  );

  const { employees, stats: s } = data;

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-6 space-y-5 max-w-7xl mx-auto print:p-2" data-testid="workshop-planning">
        {/* Header */}
        <div className="flex items-start justify-between print:mb-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900" data-testid="planning-title">{t('workshop.planning')}</h1>
            <p className="text-sm text-slate-500 mt-0.5">Vortekio — Suivi en temps reel{isToday && <span className="ml-2 inline-flex items-center gap-1 text-emerald-500 font-medium"><Sparkles className="w-3 h-3" />Live</span>}</p>
          </div>
          <div className="flex items-center gap-2 print:hidden">
            <Button variant="outline" size="sm" onClick={() => changeDate(-1)} data-testid="prev-day">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant={isToday ? 'default' : 'outline'} size="sm" onClick={() => setDate(new Date().toISOString().split('T')[0])} data-testid="today-btn">
              {new Date(date + 'T00:00').toLocaleDateString('fr-CH', { weekday: 'long', day: 'numeric', month: 'long' })}
            </Button>
            <Button variant="outline" size="sm" onClick={() => changeDate(1)} data-testid="next-day">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={loadData}><RefreshCw className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" onClick={() => window.print()} className="print:hidden"><Printer className="w-4 h-4" /></Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="planning-stats">
          <Card className="p-3 text-center">
            <p className="text-2xl font-bold text-emerald-600">{s.present}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Presents</p>
          </Card>
          <Card className="p-3 text-center">
            <p className="text-2xl font-bold text-red-400">{s.absent}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Absents</p>
          </Card>
          <Card className="p-3 text-center">
            <p className="text-2xl font-bold text-blue-600">{s.total_hours}h</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Heures</p>
          </Card>
          <Card className="p-3 text-center">
            <p className="text-2xl font-bold text-emerald-600">{s.wo_completed}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Fiches OK</p>
          </Card>
          <Card className="p-3 text-center">
            <p className="text-2xl font-bold text-amber-600">{s.wo_in_progress}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">{t('dashboard.in_progress')}</p>
          </Card>
          <Card className="p-3 text-center">
            <p className="text-2xl font-bold text-slate-900">{s.total_employees}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Effectif</p>
          </Card>
        </div>

        {/* Employee Table */}
        <div className="overflow-x-auto rounded-xl border border-slate-200" data-testid="planning-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-900 text-white">
                <th className="text-left py-3 px-4 font-medium text-xs uppercase tracking-wider">Employe</th>
                <th className="text-left py-3 px-3 font-medium text-xs uppercase tracking-wider">Poste</th>
                <th className="text-center py-3 px-3 font-medium text-xs uppercase tracking-wider w-16">{t('common.status')}</th>
                <th className="text-center py-3 px-3 font-medium text-xs uppercase tracking-wider hidden sm:table-cell">Entree</th>
                <th className="text-center py-3 px-3 font-medium text-xs uppercase tracking-wider hidden sm:table-cell">Sortie</th>
                <th className="text-center py-3 px-3 font-medium text-xs uppercase tracking-wider">Heures</th>
                <th className="text-left py-3 px-3 font-medium text-xs uppercase tracking-wider">Activite en cours</th>
                <th className="text-center py-3 px-3 font-medium text-xs uppercase tracking-wider hidden md:table-cell">Fiches</th>
                <th className="text-left py-3 px-3 font-medium text-xs uppercase tracking-wider hidden lg:table-cell">Notes</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((emp) => {
                const sc = STATUS_CONFIG[emp.status] || STATUS_CONFIG.absent;
                const rc = ROLE_COLORS[emp.role_color] || ROLE_COLORS.slate;
                return (
                  <tr key={emp.employee_id} className={`border-b border-slate-100 ${emp.status === 'absent' ? 'opacity-50' : 'hover:bg-slate-50'} transition-colors`} data-testid={`emp-row-${emp.employee_id}`}>
                    {/* Name */}
                    <td className="py-3 px-4">
                      <p className="font-semibold text-slate-900">{emp.name}</p>
                      {emp.position && <p className="text-[11px] text-slate-400">{emp.position}</p>}
                    </td>
                    {/* Role */}
                    <td className="py-3 px-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${rc}`}>{emp.role_label}</span>
                    </td>
                    {/* Status */}
                    <td className="py-3 px-3 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <span className={`w-2.5 h-2.5 rounded-full ${sc.color} ${emp.status === 'present' ? 'animate-pulse' : ''}`} />
                        <span className={`text-xs font-medium ${sc.textColor}`}>{sc.label}</span>
                      </div>
                    </td>
                    {/* Clock in */}
                    <td className="py-3 px-3 text-center hidden sm:table-cell">
                      <span className="text-xs text-slate-600 font-mono">{formatTime(emp.clock_in)}</span>
                    </td>
                    {/* Clock out */}
                    <td className="py-3 px-3 text-center hidden sm:table-cell">
                      <span className="text-xs text-slate-600 font-mono">{formatTime(emp.clock_out)}</span>
                    </td>
                    {/* Hours */}
                    <td className="py-3 px-3 text-center">
                      {emp.total_hours != null ? (
                        <span className="text-sm font-bold text-slate-900">{emp.total_hours}h</span>
                      ) : (
                        <span className="text-xs text-slate-300">-</span>
                      )}
                    </td>
                    {/* Current activity */}
                    <td className="py-3 px-3">
                      {emp.current_activity ? (
                        <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs ${emp.current_activity.type === 'fiche' ? 'bg-blue-50 text-blue-700' : emp.current_activity.type === 'pause' ? 'bg-amber-50 text-amber-700' : 'bg-violet-50 text-violet-700'}`}>
                          {emp.current_activity.type === 'fiche' && <Wrench className="w-3 h-3" />}
                          {emp.current_activity.type === 'offticket' && <Coffee className="w-3 h-3" />}
                          {emp.current_activity.type === 'pause' && <Pause className="w-3 h-3" />}
                          <span className="font-medium truncate max-w-[140px]">{emp.current_activity.label}</span>
                          {emp.current_activity.detail && <span className="text-[10px] opacity-70 truncate max-w-[100px]">{emp.current_activity.detail}</span>}
                        </div>
                      ) : emp.status !== 'absent' ? (
                        <span className="text-xs text-slate-300 italic">Aucune</span>
                      ) : null}
                      {/* Off-ticket summary (completed) */}
                      {emp.offticket_activities?.filter(o => !o.active).length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {emp.offticket_activities.filter(o => !o.active).map((o, i) => (
                            <span key={i} className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">{o.label} ({Math.round(o.duration_minutes)}min)</span>
                          ))}
                        </div>
                      )}
                    </td>
                    {/* Work orders count */}
                    <td className="py-3 px-3 text-center hidden md:table-cell">
                      {(emp.work_orders_completed > 0 || emp.work_orders_in_progress > 0) ? (
                        <div className="flex items-center justify-center gap-1">
                          <span className="text-xs font-bold text-emerald-600">{emp.work_orders_completed}</span>
                          <span className="text-[10px] text-slate-400">/</span>
                          <span className="text-xs text-amber-600">{emp.work_orders_in_progress}</span>
                        </div>
                      ) : emp.role === 'employee' ? (
                        <span className="text-xs text-slate-300">0</span>
                      ) : null}
                    </td>
                    {/* Notes */}
                    <td className="py-3 px-3 hidden lg:table-cell">
                      {editingNote === emp.employee_id ? (
                        <div className="flex gap-1">
                          <input value={noteText} onChange={e => setNoteText(e.target.value)} className="text-xs border rounded px-2 py-1 flex-1 min-w-0" placeholder="Note admin..." autoFocus onKeyDown={e => e.key === 'Enter' && saveAdminNote(emp.employee_id)} data-testid={`note-input-${emp.employee_id}`} />
                          <Button size="sm" onClick={() => saveAdminNote(emp.employee_id)} className="h-7 text-xs px-2">OK</Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingNote(null)} className="h-7 text-xs px-1">X</Button>
                        </div>
                      ) : (
                        <div className="space-y-0.5 cursor-pointer" onClick={() => { setEditingNote(emp.employee_id); setNoteText(emp.note_admin || ''); }}>
                          {emp.note_employee && (
                            <p className="text-[11px] text-blue-600 truncate max-w-[180px]" title={emp.note_employee}>
                              <MessageSquare className="w-3 h-3 inline mr-0.5" />{emp.note_employee}
                            </p>
                          )}
                          {emp.note_admin && (
                            <p className="text-[11px] text-slate-500 truncate max-w-[180px]" title={emp.note_admin}>{emp.note_admin}</p>
                          )}
                          {!emp.note_employee && !emp.note_admin && (
                            <span className="text-[10px] text-slate-300 hover:text-blue-400 transition-colors">+ Note</span>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {employees.length === 0 && (
          <Card className="p-12 text-center">
            <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">Aucun employe actif</p>
          </Card>
        )}

        {/* Vehicle Preparations Section */}
        <div className="print:break-before-page" data-testid="prep-section">
          <div className="flex items-center gap-2 mb-3">
            <Car className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-heading font-bold text-slate-900">Preparations vehicules occasion</h2>
            <Badge variant="outline" className="ml-1 text-xs">{activePreps.length} en cours</Badge>
          </div>
          {activePreps.length === 0 ? (
            <Card className="p-6 text-center">
              <p className="text-sm text-slate-400">Aucune preparation en cours</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {activePreps.map(p => (
                <Card key={p.id} className="p-3 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setPrepDialog({ open: true, vehicleId: p.vehicle_id, vehicleName: p.vehicle_name, type: p.type })}
                  data-testid={`active-prep-${p.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {p.type === 'parc'
                        ? <ClipboardList className="w-4 h-4 text-blue-600" />
                        : <Truck className="w-4 h-4 text-emerald-600" />
                      }
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{p.vehicle_name}</p>
                        <p className="text-xs text-slate-400">{p.vehicle_plate}</p>
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.type === 'parc' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                      {p.type === 'parc' ? 'Parc' : 'Livraison'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-slate-100 rounded-full h-2">
                      <div className={`h-2 rounded-full transition-all ${p.percent >= 100 ? 'bg-emerald-500' : p.type === 'parc' ? 'bg-blue-500' : 'bg-emerald-500'}`}
                        style={{ width: `${p.percent}%` }} />
                    </div>
                    <span className="text-xs font-mono text-slate-500">{p.progress}/{p.total}</span>
                    <span className="text-xs font-bold text-slate-700">{p.percent}%</span>
                  </div>
                  {p.work_order_id && (
                    <div className="mt-1.5 flex items-center gap-1 text-xs text-blue-600">
                      <Wrench className="w-3 h-3" /> Fiche de travail liee
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      <PreparationChecklist
        vehicleId={prepDialog.vehicleId}
        vehicleName={prepDialog.vehicleName}
        type={prepDialog.type}
        open={prepDialog.open}
        onClose={() => setPrepDialog(p => ({ ...p, open: false }))}
        onUpdate={loadData}
      />
    </AdminLayout>
  );
};

export default WorkshopPlanningPage;
